/**
 * Client Portal Ideas Service
 *
 * API layer for client idea review (uses client portal JWT auth).
 *
 * `reviewIdea` is the new endpoint backing the 1‑5 star rating + comment
 * UI. The legacy `approveIdea` / `rejectIdea` endpoints remain on the
 * backend (admin still uses the lifecycle); the client portal UI no
 * longer calls them.
 */

import { getApiBaseUrl } from "@/utils/apiUtils";
import type { Idea } from "@/domains/Ideas/types";

const API_BASE_URL = getApiBaseUrl();

/**
 * The condensed client-portal payload: a field-limited subset of the
 * one ``Idea`` model (TD-030) — no tasks, notes, briefId or feedback,
 * but the same shape for everything it does carry.
 */
export type ClientIdea = Pick<
  Idea,
  | "id"
  | "title"
  | "contentType"
  | "objectiveType"
  | "resources"
  | "body"
  | "status"
  | "createdAt"
  | "clientRating"
  | "clientComment"
  | "clientReviewedAt"
>;

export interface ClientIdeasResponse {
  ideas: ClientIdea[];
}

/** Fields the client fills in when proposing an idea from the portal. */
export interface CreateClientIdeaInput {
  title: string;
  description?: string;
  link?: string;
}

class ClientIdeasService {
  private getAuthHeaders(token: string): HeadersInit {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || `HTTP error ${response.status}`);
    }
    return response.json();
  }

  /**
   * Condensed PENDING-only payload. Used by the dashboard tile to show
   * "you have N ideas to review" — the action prompt count.
   */
  async getPendingIdeas(token: string): Promise<ClientIdea[]> {
    const response = await fetch(`${API_BASE_URL}/api/client-portal/ideas`, {
      method: "GET",
      headers: this.getAuthHeaders(token),
    });
    const data = await this.handleResponse<ClientIdeasResponse>(response);
    return data.ideas;
  }

  /**
   * Condensed READY payload — ideas that are approved and now in
   * production. Read-only "in produzione" surface, separate from the
   * PENDING review queue so it does not affect the to-review count.
   */
  async getReadyIdeas(token: string): Promise<ClientIdea[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/client-portal/ideas/ready`,
      {
        method: "GET",
        headers: this.getAuthHeaders(token),
      },
    );
    const data = await this.handleResponse<ClientIdeasResponse>(response);
    return data.ideas;
  }

  /**
   * Propose a new idea from the portal. Lands as a DRAFT in the agency
   * pipeline, tagged as client-submitted. Fire-and-forget: the created
   * idea is returned but the review list is not expected to show it.
   */
  async createIdea(
    token: string,
    input: CreateClientIdeaInput,
  ): Promise<ClientIdea> {
    const response = await fetch(`${API_BASE_URL}/api/client-portal/ideas`, {
      method: "POST",
      headers: this.getAuthHeaders(token),
      body: JSON.stringify(input),
    });
    return this.handleResponse<ClientIdea>(response);
  }

  /**
   * Set the client's rating + comment on an idea. Pass `null` (or empty
   * string for comment) to clear. Rating and comment are independent —
   * one can be set without the other.
   */
  async reviewIdea(
    token: string,
    ideaId: string,
    review: { rating?: number | null; comment?: string | null },
  ): Promise<ClientIdea> {
    const response = await fetch(
      `${API_BASE_URL}/api/client-portal/ideas/${ideaId}/review`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(review),
      },
    );
    return this.handleResponse<ClientIdea>(response);
  }

  // ---------------------------------------------------------------------
  // Legacy approve / reject — retained for backward compat during the
  // migration but no longer wired into the client portal UI. Admin still
  // controls the status lifecycle via the admin app, not these.
  // ---------------------------------------------------------------------
  async approveIdea(token: string, ideaId: string): Promise<ClientIdea> {
    const response = await fetch(
      `${API_BASE_URL}/api/client-portal/ideas/${ideaId}/approve`,
      {
        method: "POST",
        headers: this.getAuthHeaders(token),
      },
    );
    return this.handleResponse<ClientIdea>(response);
  }

  async rejectIdea(
    token: string,
    ideaId: string,
    feedback: string,
  ): Promise<ClientIdea> {
    const response = await fetch(
      `${API_BASE_URL}/api/client-portal/ideas/${ideaId}/reject`,
      {
        method: "POST",
        headers: this.getAuthHeaders(token),
        body: JSON.stringify({ feedback }),
      },
    );
    return this.handleResponse<ClientIdea>(response);
  }
}

export const clientIdeasService = new ClientIdeasService();
export default clientIdeasService;
